import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSettings } from '../lib/api';
import {
  LayoutDashboard, FileSpreadsheet, Upload, AlertTriangle,
  Users, UserCheck, Truck, MapPin, FileText, MessageSquare,
  Settings, LogOut, Menu, X, Search,
  Bell, ChevronDown, Package, Shield, Plus
} from 'lucide-react';
import { cn } from '../lib/utils';

const NAV_ITEMS = [
  { section: 'Home', items: [
    { path: '/boss/home', label: 'Dashboard', icon: LayoutDashboard },
  ]},
  { section: 'Finance & Accounting', items: [
    { path: '/boss/fa/import', label: 'Import Invoices', icon: Upload },
    { path: '/boss/fa/invoices', label: 'Invoice Register', icon: FileSpreadsheet },
    { path: '/boss/fa/missing', label: 'Missing Invoices', icon: AlertTriangle },
  ]},
  { section: 'Sales & CRM', items: [
    { path: '/boss/scrm/parties', label: 'Parties', icon: Users },
    { path: '/boss/scrm/salesmen', label: 'Salesmen', icon: UserCheck },
    { path: '/boss/scrm/products', label: 'Products', icon: Package },
  ]},
  { section: 'Supply Chain', items: [
    { path: '/boss/scl/transports', label: 'Transports', icon: Truck },
    { path: '/boss/scl/stations', label: 'Stations', icon: MapPin },
    // altPath: SCRM_PARTY has dm/documents but not scl/bilty (merged into
    // this single item) — without checking both, they'd lose sidebar access
    // to Documents entirely. See BiltyDocsPage.js for the same reasoning.
    { path: '/boss/scl/bilty', altPath: '/boss/dm/documents', label: 'Bilty Docs', icon: FileText },
    // permPath/altPath: the link target /boss/logs is a brand-new URL that
    // matches neither original permission string, so — unlike Bilty Docs,
    // where the link target itself was still a valid permission key —
    // permission-checking must be pointed at the two originals explicitly.
    { path: '/boss/logs', permPath: '/boss/fa/import/history', altPath: '/boss/scl/whatsapp', label: 'Logs', icon: MessageSquare },
  ]},
  { section: 'System', items: [
    { path: '/boss/settings/users', label: 'Users', icon: Shield },
  ]},
];

export default function AppShell() {
  const { user, logout, isNavAllowed, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  // No notifications backend exists yet — this stays empty rather than
  // showing fake/mocked items. Wire up a real source here when one exists.
  const notifications = [];

  // Tenant company name — was hardcoded "Himalaya Global" in the header
  // before; now pulled from Settings (backend/app/routers/settings.py),
  // the same data source the Settings page itself edits.
  const [companyName, setCompanyName] = useState('');
  useEffect(() => {
    getSettings().then(r => setCompanyName(r.data?.company_name || '')).catch(() => {});
  }, []);

  // Filter nav items based on RBAC
  const filteredNav = NAV_ITEMS.map(group => {
    const filteredItems = group.items.filter(item => {
      // System section: admin only
      if (group.section === 'System') return isAdmin();
      // Otherwise check RBAC nav permissions
      return isNavAllowed(item.permPath || item.path) || (item.altPath && isNavAllowed(item.altPath));
    });
    return { ...group, items: filteredItems };
  }).filter(group => group.items.length > 0);

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "flex flex-col bg-white transition-all duration-300 z-50",
        "fixed lg:relative h-full",
        sidebarOpen ? "w-44" : "w-[68px]",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo */}
        <div className="flex items-center justify-center h-[71px] px-4">
          <img src="/boss-logo.svg" alt="BoSS" className="w-12 h-12 object-contain flex-shrink-0" />
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin" data-testid="sidebar-nav">
          {filteredNav.map((group) => (
            <div key={group.section} className="mb-1">
              {sidebarOpen && (
                <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-boss-text-muted">
                  {group.section}
                </div>
              )}
              {group.items.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-2 mx-1.5 px-2.5 py-2 rounded-lg text-sm transition-colors",
                      // Brand blue (#0066FF), matching boss-logo.svg — not
                      // boss-violet, which is the inherited Himalaya Global
                      // token, not the actual BoSS brand color.
                      isActive
                        ? "bg-[#0066FF] text-white"
                        : "text-boss-text-muted hover:bg-boss-surface-2 hover:text-boss-text"
                    )}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    {sidebarOpen && <span className="truncate">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Toggle */}
        <div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex items-center justify-center w-full h-10 text-boss-text-muted hover:text-boss-text"
          >
            <Menu size={18} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-[71px] bg-white flex items-center px-4 gap-3 flex-shrink-0">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)}>
            <Menu size={20} className="text-boss-text" />
          </button>

          <div className="hidden sm:flex items-center gap-2 px-1">
            <img src="/hg-logo-crop.png" alt="HG" className="w-14 h-14 object-contain" />
            <span className="text-lg font-medium text-boss-text">{companyName || 'Himalaya Global'}</span>
          </div>

          <div className="flex-1 max-w-md mx-auto">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-boss-text-muted" />
              <input
                type="text"
                placeholder="Search invoices, parties, orders..."
                className="w-full pl-11 pr-4 py-3 rounded-lg border border-boss-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-boss-violet/30"
                data-testid="global-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/boss/fa/import"
              className="p-3 rounded-lg bg-boss-surface-2 hover:bg-boss-surface-3 transition-colors"
              title="Import Invoices"
            >
              <Plus size={18} className="text-boss-text" />
            </Link>

            <Link
              to="/boss/settings"
              className="p-3 rounded-lg bg-boss-surface-2 hover:bg-boss-surface-3 transition-colors"
              title="Settings"
            >
              <Settings size={18} className="text-boss-text" />
            </Link>

            <div className="relative">
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-3 rounded-lg bg-boss-surface-2 hover:bg-boss-surface-3 transition-colors"
                data-testid="notifications-btn"
              >
                <Bell size={18} className="text-boss-text" />
                {notifications.length > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-boss-danger rounded-full"></span>
                )}
              </button>

              {notifOpen && (
                <>
                  <button className="fixed inset-0 z-40 cursor-default" onClick={() => setNotifOpen(false)} tabIndex={-1} aria-label="Close menu" />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-boss-border rounded-lg shadow-lg z-50">
                    <div className="px-4 py-3 border-b border-boss-border">
                      <span className="text-sm font-semibold text-boss-text">Notifications</span>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 flex flex-col items-center text-center gap-2">
                        <Bell size={22} className="text-boss-text-muted/50" />
                        <span className="text-sm text-boss-text-muted">No new notifications</span>
                      </div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.map((n) => (
                          <div key={n.id} className="px-4 py-3 border-b border-boss-border last:border-0 hover:bg-boss-surface-2">
                            <div className="text-sm text-boss-text">{n.message}</div>
                            <div className="text-xs text-boss-text-muted mt-0.5">{n.time}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 rounded-full border border-boss-border bg-white pl-1.5 pr-3 py-1.5 hover:bg-boss-surface-2 transition-colors"
                data-testid="profile-menu-btn"
              >
                <div className="w-9 h-9 rounded-full bg-boss-info-soft flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-boss-info">
                    {user?.name?.[0] || 'A'}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-boss-text leading-tight">{user?.name || 'Admin'}</div>
                  <div className="text-[10px] text-boss-text-muted leading-tight">{user?.role?.replace('_', ' ') || 'Admin'}</div>
                </div>
                <ChevronDown size={16} className="text-boss-text-muted flex-shrink-0" />
              </button>

              {profileOpen && (
                <>
                  <button className="fixed inset-0 z-40 cursor-default" onClick={() => setProfileOpen(false)} tabIndex={-1} aria-label="Close menu" />
                  <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-boss-border rounded-lg shadow-lg z-50 py-1">
                    <button
                      onClick={logout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-boss-text hover:bg-boss-surface-2 transition-colors"
                      data-testid="logout-btn"
                    >
                      <LogOut size={16} className="text-boss-text-muted" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
